// ═══════════════════════════════════════════════════════════════
// ImpactPortalBulk — Bulk entry tab
// Mobile-native HTML/CSS. No GabComponents.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useImpactStore } from '../../../store/impactStore';
import { reverseGeocode } from '../lib/gis';
import { IconMapPin, IconCamera, IconX } from '@tabler/icons-react';
import { AddressAutocomplete } from '../../../components/ui/AddressAutocomplete';
import '../../shelter/portal/ShelterPortal.css';
import './ImpactPortal.css';

// ── Category config ───────────────────────────────────────────

const DAMAGE_CATEGORIES = [
  { key: 'affected',     label: 'Affected',     color: '#16a34a' },
  { key: 'minor',        label: 'Minor',        color: '#d97706' },
  { key: 'major',        label: 'Major',        color: '#ea580c' },
  { key: 'destroyed',    label: 'Destroyed',    color: '#B12525' },
  { key: 'inaccessible', label: 'Inaccessible', color: '#7c3aed' },
];

type BulkView = 'form' | 'confirm' | 'submitting' | 'success';

interface ImpactPortalBulkProps {
  onViewReports: () => void;
}

export default function ImpactPortalBulk({ onViewReports }: ImpactPortalBulkProps) {
  const { activeIncident, user } = useAuthStore();
  const { bulkCreateAssessments } = useImpactStore();

  // View
  const [view, setView] = useState<BulkView>('form');

  // Form
  const [neighborhood, setNeighborhood] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>(
    Object.fromEntries(DAMAGE_CATEGORIES.map(c => [c.key, 0]))
  );

  // GPS / address
  const [gpsStatus, setGpsStatus] = useState<'acquiring' | 'locked' | 'error'>('acquiring');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const geocodedRef = useRef(false);

  // Photos (reference only — not uploaded to individual records)
  const [photoThumbs, setPhotoThumbs] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - photoFiles.length);
    const thumbs = await Promise.all(
      files.map(f => new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target?.result as string);
        reader.readAsDataURL(f);
      }))
    );
    setPhotoFiles(prev => [...prev, ...files]);
    setPhotoThumbs(prev => [...prev, ...thumbs]);
    e.target.value = '';
  };

  const removePhoto = (i: number) => {
    setPhotoFiles(prev => prev.filter((_, idx) => idx !== i));
    setPhotoThumbs(prev => prev.filter((_, idx) => idx !== i));
  };

  // Progress
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  // GPS watch
  useEffect(() => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setGpsStatus('locked');
        if (!geocodedRef.current) {
          geocodedRef.current = true;
          try {
            const result = await reverseGeocode(lat, lng);
            if (result?.address) setAddress(result.address);
          } catch { /* ignore */ }
        }
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const totalHomes = Object.values(counts).reduce((sum, n) => sum + (n || 0), 0);

  const nonZeroCategories = DAMAGE_CATEGORIES.filter(c => (counts[c.key] || 0) > 0);

  const stepCount = (key: string, delta: number) => {
    setCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  };

  const handleSubmit = async () => {
    if (!activeIncident) return;
    setView('submitting');
    setProgress(0);
    setError('');

    try {
      const items = nonZeroCategories.map(cat => ({
        incidentId:     String(activeIncident.id),
        assessmentType: 'individual',
        damageCategory: cat.key,
        homeCount:      counts[cat.key],
        address:        address || '',
        latitude:       coords?.lat ?? null,
        longitude:      coords?.lng ?? null,
        neighborhood:   neighborhood.trim(),
        description:    neighborhood.trim() ? `Bulk entry — ${neighborhood.trim()}` : 'Bulk entry',
        method:         'bulk',
        bulkEntry:      true,
        assessorName:   user?.fullName || '',
        assessorEmail:  user?.email || '',
      }));

      let completed = 0;
      for (const item of items) {
        await bulkCreateAssessments([item]);
        completed++;
        setProgress(Math.round((completed / items.length) * 100));
      }
      setView('success');
    } catch {
      setError('Submission failed. Please try again.');
      setView('confirm');
    }
  };

  const resetForm = () => {
    setNeighborhood('');
    setCounts(Object.fromEntries(DAMAGE_CATEGORIES.map(c => [c.key, 0])));
    setAddress('');
    setPhotoFiles([]);
    setPhotoThumbs([]);
    geocodedRef.current = false;
    setError('');
    setProgress(0);
    setView('form');
  };

  // ── Success ───────────────────────────────────────────────────

  if (view === 'success') {
    return (
      <div className="ip-success">
        <div className="ip-success-icon">✅</div>
        <div className="ip-success-title">Bulk Entry Submitted!</div>
        <div className="ip-success-sub">
          {nonZeroCategories.length} assessment record{nonZeroCategories.length !== 1 ? 's' : ''} created covering {totalHomes} home{totalHomes !== 1 ? 's' : ''}.
        </div>
        <div className="ip-success-btns">
          <button className="ip-vida-btn" style={{ height: 52, fontSize: 16 }} onClick={resetForm}>
            New Entry
          </button>
          <button className="ip-quick-btn" onClick={onViewReports}>
            View Reports
          </button>
        </div>
      </div>
    );
  }

  // ── Confirm step ─────────────────────────────────────────────

  if (view === 'confirm') {
    return (
      <div className="ip-bulk-wrap">
        <div style={{ paddingTop: 8 }}>
          <div className="ip-section-label">Confirm Bulk Entry</div>
        </div>

        {error && <div className="sp-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="ip-confirm-card" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#7B7D7F', marginBottom: 16 }}>
            {neighborhood.trim() ? neighborhood.trim() : 'Unnamed area'} — {totalHomes} home{totalHomes !== 1 ? 's' : ''}
          </div>
          <table className="ip-confirm-table">
            <tbody>
              {nonZeroCategories.map(cat => (
                <tr key={cat.key}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: cat.color }} />
                      {cat.label}
                    </span>
                  </td>
                  <td>{counts[cat.key]}</td>
                </tr>
              ))}
              <tr>
                <td style={{ fontWeight: 700, paddingTop: 16 }}>Total</td>
                <td style={{ fontWeight: 700, paddingTop: 16 }}>{totalHomes}</td>
              </tr>
            </tbody>
          </table>
          {address && (
            <div style={{ fontSize: 12, color: '#7B7D7F', marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <IconMapPin size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{address}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="ip-quick-btn" style={{ flex: 1 }} onClick={() => setView('form')}>
            Go Back
          </button>
          <button className="ip-submit-btn" style={{ flex: 2, marginTop: 0 }} onClick={handleSubmit}>
            Confirm &amp; Submit
          </button>
        </div>
      </div>
    );
  }

  // ── Submitting ────────────────────────────────────────────────

  if (view === 'submitting') {
    return (
      <div style={{ padding: '40px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B1C1C', marginBottom: 8 }}>
            Creating assessments…
          </div>
          <div style={{ fontSize: 14, color: '#7B7D7F' }}>{progress}% complete</div>
        </div>
        <div className="ip-progress-bar-track">
          <div className="ip-progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────

  return (
    <div className="ip-bulk-wrap">

      {/* Neighborhood */}
      <div className="ip-bulk-card">
        <div className="ip-bulk-card-title">Area / Neighborhood</div>
        <input
          className="sp-input"
          value={neighborhood}
          onChange={e => setNeighborhood(e.target.value)}
          placeholder="e.g., Riverside Estates, Block 400"
        />
      </div>

      {/* Category steppers */}
      <div className="ip-bulk-card">
        <div className="ip-bulk-card-title">Homes by Damage Category</div>
        {DAMAGE_CATEGORIES.map(cat => (
          <div key={cat.key} className="ip-stepper-row">
            <div className="ip-stepper-label">
              <div className="ip-report-dot" style={{ background: cat.color, marginTop: 0 }} />
              {cat.label}
            </div>
            <div className="ip-stepper">
              <button
                className="ip-stepper-btn"
                onClick={() => stepCount(cat.key, -1)}
                disabled={counts[cat.key] === 0}
              >
                −
              </button>
              <div className="ip-stepper-val">{counts[cat.key]}</div>
              <button
                className="ip-stepper-btn"
                onClick={() => stepCount(cat.key, 1)}
              >
                +
              </button>
            </div>
          </div>
        ))}
        {totalHomes > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <div className="ip-total-badge">Total: {totalHomes} home{totalHomes !== 1 ? 's' : ''}</div>
          </div>
        )}
      </div>

      {/* Location */}
      <div className="ip-bulk-card">
        <div className="ip-bulk-card-title">
          <IconMapPin size={15} />
          Location
        </div>
        <div className={`vida-gps-bar ${gpsStatus}`} style={{ marginBottom: 8 }}>
          <IconMapPin size={14} />
          {gpsStatus === 'acquiring' && <span>Acquiring GPS…</span>}
          {gpsStatus === 'locked'    && <span>GPS locked</span>}
          {gpsStatus === 'error'     && <span>GPS unavailable</span>}
        </div>
        <AddressAutocomplete
          value={address}
          onChange={(val, lat, lng) => {
            setAddress(val);
            if (lat !== undefined && lng !== undefined) {
              setCoords({ lat, lng });
              geocodedRef.current = true;
            }
          }}
          placeholder="Address or area description"
        />
      </div>

      {/* Reference Photos */}
      <div className="ip-bulk-card">
        <div className="ip-bulk-card-title">Reference Photos (optional)</div>
        <div style={{ fontSize: 12, color: '#939598', marginBottom: 8 }}>
          Photos are for field reference only — not attached to individual records.
        </div>
        {photoThumbs.length > 0 && (
          <div className="ip-photo-thumbs" style={{ marginBottom: 8 }}>
            {photoThumbs.map((thumb, i) => (
              <div key={i} className="ip-photo-thumb">
                <img src={thumb} alt={`Photo ${i + 1}`} />
                <button className="ip-photo-remove" onClick={() => removePhoto(i)}>
                  <IconX size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        {photoFiles.length < 10 && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoAdd}
            />
            <div className="ip-photo-tap" onClick={() => fileInputRef.current?.click()}>
              <IconCamera size={22} />
              <span>{photoFiles.length === 0 ? 'Tap to add photo' : `Add another (${photoFiles.length}/10)`}</span>
            </div>
          </>
        )}
      </div>

      <button
        className="ip-submit-btn"
        disabled={totalHomes === 0}
        onClick={() => setView('confirm')}
      >
        {totalHomes > 0
          ? `Review — Submit ${totalHomes} Home${totalHomes !== 1 ? 's' : ''}`
          : 'Enter at least 1 home'}
      </button>
    </div>
  );
}
